import { useLayoutEffect, useRef, useState } from 'react';
import { TableProps, RowConfig } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { EmptyTableState } from './EmptyTableState';
import { getTableCellProps, getColumnMinWidthPx } from '../../utils/cellStyles';

interface TableContentProps<T = unknown> extends Omit<
  TableProps<T>,
  'toolbar' | 'pagination' | 'onSort' | 'onFilter'
> {
  /** Si devuelve true, el checkbox de selección de esa fila se deshabilita (además de `row.disabled`). */
  isRowSelectionDisabled?: (row: RowConfig<T>) => boolean;
  selectedIds?: (string | number)[];
  allSelected?: boolean;
  someSelected?: boolean;
  resizeHoverColumnId?: string | null;
  onSelectAll?: (selected: boolean) => void;
  onRowSelect?: (row: RowConfig<T>) => void;
  onSort?: (columnId: string) => void;
  onFilter?: (columnId: string) => void;
  onLock?: (columnId: string) => void;
  onResize?: (columnId: string, width: number, defaultWidthAtStart?: number) => void;
  onResizeDoubleClick?: (columnId: string) => void;
  onResizeHover?: (columnId: string, isHovering: boolean) => void;
  onCellFocus?: (rowId: string | number, columnId: string) => void;
  isCellFocused?: (rowId: string | number, columnId: string) => boolean;
  onCellChange?: (rowId: string | number, columnId: string, value: unknown) => void;
  /** Clave que cambia cuando cambian las filas mostradas (p. ej. paginación); fuerza recalcular anchos auto. */
  rowsDataKey?: string;
}

export const TableContent = <T, >({
  columns,
  rows,
  loading = false,
  empty,
  selection,
  sort,
  filters = [],
  expandable = false,
  resizable = false,
  columnsLockable,
  resizeHoverColumnId = null,
  striped = false,
  hoverable = true,
  dense = false,
  stickyHeader = true,
  stickyCheckbox = true,
  stickyActions = true,
  className,
  selectedIds = [],
  allSelected = false,
  someSelected = false,
  onSelectAll,
  onRowSelect,
  onSort,
  onFilter,
  onLock,
  onResize,
  onResizeDoubleClick,
  onResizeHover,
  onRowClick,
  onRowExpand,
  onCellFocus,
  isCellFocused,
  onCellChange,
  rowsDataKey,
  isRowSelectionDisabled,
}: TableContentProps<T>) => {
  const rowsList = rows ?? [];
  const visibleColumns = columns.filter((col) => col.visible !== false);
  const hasColumnMaxWidthAuto = visibleColumns.some((c) => c.maxWidth === 'auto');

  /** Anchos reales de `<th data-column-id>` medidos fuera del render (ResizeObserver); sin guardar anchos en estado. */
  const columnWidthsRef = useRef<Map<string, number>>(new Map());
  const theadRef = useRef<HTMLTableSectionElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const isEmpty = rowsList.length === 0 && !loading;

  useLayoutEffect(() => {
    if (isEmpty || !theadRef.current || !tableRef.current) return;

    const measure = () => {
      const thead = theadRef.current;
      const table = tableRef.current;
      if (!thead || !table) return;

      const map = columnWidthsRef.current;
      map.clear();
      thead.querySelectorAll<HTMLElement>('th[data-column-id]').forEach((th) => {
        const id = th.dataset.columnId;
        if (id) map.set(id, th.getBoundingClientRect().width);
      });

      // Calcular y aplicar offsets directamente al DOM vía variables CSS
      const visibleCols = columns.filter((col) => col.visible !== false);
      const sortedCols = [
        ...visibleCols.filter((col) => col.locked),
        ...visibleCols.filter((col) => !col.locked),
      ];

      // Sticky Left
      let currentLeft = 0;
      table.style.setProperty('--table-sticky-left-selection', '0px');
      if (selection?.enabled) {
        currentLeft += 48; // checkbox column width
      }
      table.style.setProperty('--table-sticky-left-expand', `${currentLeft}px`);
      if (expandable) {
        currentLeft += 48; // expand column width
      }

      for (const col of sortedCols) {
        if (col.locked) {
          table.style.setProperty(`--table-sticky-left-${col.id}`, `${currentLeft}px`);
          currentLeft += map.get(String(col.id)) ?? 150;
        } else {
          table.style.removeProperty(`--table-sticky-left-${col.id}`);
        }
      }

      // Sticky Right
      let currentRight = 0;
      for (let i = sortedCols.length - 1; i >= 0; i--) {
        const col = sortedCols[i];
        if (col.type === 'actions') {
          table.style.setProperty(`--table-sticky-right-${col.id}`, `${currentRight}px`);
          currentRight += map.get(String(col.id)) ?? 120;
        } else {
          table.style.removeProperty(`--table-sticky-right-${col.id}`);
        }
      }
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(theadRef.current);

    return () => ro.disconnect();
  }, [columns, selection?.enabled, expandable, isEmpty, loading, rowsDataKey]);

  const tableMinWidthPx =
    (selection?.enabled ? 48 : 0) +
    (expandable ? 48 : 0) +
    visibleColumns.reduce(
      (sum, col) =>
        sum +
        getColumnMinWidthPx(
          col as Parameters<typeof getColumnMinWidthPx>[0],
          col.type === 'actions',
          hasColumnMaxWidthAuto,
        ),
      0,
    );

  // Con table-layout: auto + width:max-content la tabla toma el tamaño real de las columnas;
  // el contenedor con overflow-x-auto permite scroll horizontal si no cabe.
  // Si alguna columna tiene maxWidth: 'auto', tabla usa width:100% y table-layout:fixed para que
  // los anchos explícitos (y el resize) se respeten; solo esa columna queda sin width y toma el resto.
  const baseClasses = cn(
    'ui-border-separate [border-spacing:0]',
    resizable
      ? hasColumnMaxWidthAuto
        ? 'ui-table-fixed ui-w-full'
        : 'ui-table-auto ui-w-max'
      : 'ui-w-full',
    striped && 'ui-table-striped',
    hoverable && 'ui-table-hover',
    dense && 'ui-table-dense',
    className,
  );

  const containerClasses = cn(
    'ui-w-full ui-min-w-0 ui-h-full ui-overflow-x-auto ui-overflow-y-auto custom-scrollbar',
    /** Evita desfase entre `<thead>` y `<tbody>` cuando aparece scrollbar vertical (misma anchura útil). */
    '[scrollbar-gutter:stable]',
    'ui-pb-16', // padding-bottom para crear espacio arriba del scrollbar horizontal
  );

  // Cuando no hay filas y no está cargando, renderizar el empty state fuera de la tabla
  // para que se centre correctamente en el contenedor visible (no en el ancho total de columnas)

  return (
    <div className={containerClasses}>
      <style>
        {resizeHoverColumnId &&
          `
          td[data-column-id="${resizeHoverColumnId}"],
          th[data-column-id="${resizeHoverColumnId}"] {
            box-shadow: inset -2px 0 0 0 var(--ui-color-bg-surface-tertiary-default) !important;
          }
        `}
        {`
          tr[data-editing="true"] td {
            box-shadow: inset 0 1px 0 0 var(--ui-color-border-brand), inset 0 -1px 0 0 var(--ui-color-border-brand) !important;
          }
        `}
      </style>
      {!isEmpty && (
        <table
          ref={tableRef}
          className={baseClasses}
          style={
            resizable
              ? hasColumnMaxWidthAuto
                ? { minWidth: tableMinWidthPx }
                : { minWidth: 'max-content' }
              : undefined
          }
        >
          <colgroup>
            {selection?.enabled && <col style={{ width: 48, minWidth: 48 }} />}
            {expandable && <col style={{ width: 48, minWidth: 48 }} />}
            {visibleColumns.map((col) => {
              const props = getTableCellProps(
                col as Parameters<typeof getTableCellProps>[0],
                0,
                undefined,
                col.type === 'actions',
                hasColumnMaxWidthAuto,
              );
              const { width, minWidth } = props.style || {};
              return <col key={col.id} style={{ width, minWidth }} />;
            })}
          </colgroup>
          {(rowsList.length > 0 || loading) && (
            <TableHeader
              ref={theadRef}
              columnWidthsRef={columnWidthsRef}
              columns={columns}
              sortConfig={sort}
              filters={filters}
              showSelection={selection?.enabled}
              showExpand={expandable}
              allSelected={allSelected}
              someSelected={someSelected}
              resizable={resizable}
              stickyHeader={stickyHeader}
              stickyCheckbox={stickyCheckbox}
              stickyActions={stickyActions}
              onSelectAll={onSelectAll}
              onSort={onSort}
              onFilter={onFilter}
              onLock={onLock}
              onResize={onResize}
              onResizeDoubleClick={onResizeDoubleClick}
              onResizeHover={onResizeHover}
              rowsDataKey={rowsDataKey}
              hasColumnMaxWidthAuto={hasColumnMaxWidthAuto}
              columnsLockable={columnsLockable}
            />
          )}
          <TableBody
            rows={rowsList}
            columns={columns}
            columnWidthsRef={columnWidthsRef}
            loading={loading}
            empty={empty}
            selectedIds={selectedIds}
            expandable={expandable}
            showSelection={selection?.enabled}
            showExpand={expandable}
            stickyCheckbox={stickyCheckbox}
            stickyActions={stickyActions}
            onRowClick={onRowClick}
            onRowExpand={onRowExpand}
            onRowSelect={onRowSelect}
            onCellFocus={onCellFocus}
            isCellFocused={isCellFocused}
            onCellChange={onCellChange}
            hasColumnMaxWidthAuto={hasColumnMaxWidthAuto}
            isRowSelectionDisabled={isRowSelectionDisabled}
          />
        </table>
      )}
      {isEmpty && empty && <EmptyTableState {...empty} />}
    </div>
  );
};
